import React from "react";
import cx from "classnames";
import { Meta, Text } from "../../Type";
import { useEventDetailContext } from "../../../contexts/eventDetailProvider";
import { useAuth } from "../../../contexts/AuthProvider";
import { useOrganizationContext } from "../../../contexts/OrganizationProvider";
import { coordWeldMap } from "../../../helpers/coordWeldMap";
import {
  MILLISECONDS_IN_SECONDS,
  METERS_IN_KM
} from "../../../helpers/constants";
import styles from "./index.module.scss";
import DateTime from "../../DateTime";
import { kpToSp, kpToMp, kpToFt } from "../../AnalysisCharts/BaseChart";
import { PigUpdateMetadata } from "@hifieng/common/dist/types/graphql";
import { toCamelCase } from "../../../helpers/toCamelCase";

export const detectIOS = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (
    userAgent.search("iphone") > -1 ||
    userAgent.search("ipad") > -1 ||
    userAgent.search("ipod") > -1
  ) {
    return true;
  } else {
    return false;
  }
};
const isIOS = detectIOS();

export const copyToClipboard = () => {
  // Get the text field
  const copyText = document.getElementById("copyCoordinates");

  // Select the text field
  if (copyText !== null) {
    const inner = copyText.innerText;
    if (inner !== undefined) {
      // Copy the text inside the text field
      navigator.clipboard.writeText(inner);
      // Alert the copied text
      /* eslint-disable no-alert */
      alert(`Copied the coordinates ${inner} to clipboard.`);
    }
  }
};

/**
 * Validates if a string is a valid Unix timestamp in seconds (10 digits).
 * Unix timestamps should be in seconds, not milliseconds.
 * @param {string | null | undefined} timestampStr - The timestamp string to validate
 * @returns {boolean} True if valid 10-digit Unix timestamp, false otherwise
 */
export function isUnixTimestampString(
  timestampStr: string | null | undefined
): boolean {
  // console.log("isUnixTimestampString", timestampStr);
  if (!timestampStr) {
    return false;
  }
  const trimmed = timestampStr.trim();
  console.log("trimmed timestamp", trimmed);

  // This component converts the value from seconds to milliseconds,
  // so only accept second-based Unix timestamps.
  // checks for 10 digit UNIX timestamps, which is seconds.
  if (!/^\d{10}$/.test(trimmed)) {
    return false;
  }
  const timestamp = Number(trimmed);

  return Number.isInteger(timestamp) && timestamp > 0;
}

/* eslint-disable @typescript-eslint/camelcase */
const PIG_METADATA_DISPLAY_NAME_MAPPING: Record<string, string> = {
  pigNotificationType: "Notification Type",
  speed: "Speed",
  pigPointOfInterestEta: "Next POI ETA",
  nextPointOfInterest: "Next POI",
  upcomingValveType: "Next POI Type",
  previousPointOfInterest: "Previous POI",
  previousValveType: "Previous POI Type",
  pigReceiptEta: "ETA at Receipt",
  toolType: "Tool Type",
  startTime: "Time at Current Location",
  endTime: "End Time",
  kp: "KP",
  coordinates: "Coordinates",
  channel: "Channel",
  pigRunIdInfo: "Pig Run ID Info",
  pigRunId: "Pig Run ID"
};

//cSpell:disable
const PIG_DATA_IMPORT_NAME_MAPPING: Record<string, string> = {
  pignotificationtype: "pigNotificationType",
  pignotifftype: "pigNotificationType",

  speed: "speed",

  pigpointofinteresteta: "pigPointOfInterestEta",
  pigpoieta: "pigPointOfInterestEta",

  nextpointofinterest: "nextPointOfInterest",
  pignextpoi: "nextPointOfInterest",

  upcomingvalvetype: "upcomingValveType",
  pignextvalve: "upcomingValveType",

  previouspointofinterest: "previousPointOfInterest",
  pigprevpoi: "previousPointOfInterest",

  previousvalvetype: "previousValveType",
  pigprevvalve: "previousValveType",

  pigreceipteta: "pigReceiptEta",
  pigreceta: "pigReceiptEta",

  tooltype: "toolType",

  pigrunidinfo: "pigRunIdInfo",
  pigrunid: "pigRunId"
};
//cSpell:enable

// Manual display order (case-insensitive)
const PIG_METADATA_MANUAL_ORDER = [
  "startTime",
  "kp",
  "coordinates",
  "pigNotificationType",
  "status",
  "speed",
  "pigPointOfInterestEta",
  "nextPointOfInterest",
  "upcomingValveType",
  "previousPointOfInterest",
  "previousValveType",
  "pigReceiptEta",
  "pigRunIdInfo",
  "pigRunId",
  "channel"
];

export type ParsePigUpdateMetadataInput = {
  pigUpdateMetadata?: Array<PigUpdateMetadata> | null;
  channel: string;
  startTime: number;
  kp: number;
  coordinates: string;
  pigRunId?: string | null;
  endTime?: number | null;
  weldId?: string | null;
  speed?: number | null;
  nextPoi?: string | null;
  arrivalEstimate?: number | null;
};

export type PigMetadataRow = {
  name: string;
  value: string;
  isTimestamp: boolean;
  originalKey: string;
  isKp: boolean;
  isCoordinates: boolean;
};

export type ParsedPigUpdateMetadata = {
  rows: Array<PigMetadataRow>;
  hasNextPoiEta: boolean;
  hasNextPoi: boolean;
  hasSpeed: boolean;
};

const PIG_METADATA_ORDER_INDEX = new Map(
  PIG_METADATA_MANUAL_ORDER.map((name, i) => [name.toLowerCase(), i])
);

function getPigMetadataOrder(originalKey: string): number {
  const key = originalKey.trim().toLowerCase();
  return PIG_METADATA_ORDER_INDEX.has(key)
    ? (PIG_METADATA_ORDER_INDEX.get(key) as number)
    : Number.MAX_SAFE_INTEGER;
}

function normalizeImportName(rawName: string): string {
  return rawName
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function normalizePigMetadataKey(rawName: string): string {
  const compact = rawName.trim().replace(/\s+/g, " ");
  const normalized = normalizeImportName(compact);

  return (
    PIG_DATA_IMPORT_NAME_MAPPING[normalized] ||
    toCamelCase(compact.replace(/_/g, " "))
  );
}

function toDisplayName(key: string): string {
  if (PIG_METADATA_DISPLAY_NAME_MAPPING[key]) {
    return PIG_METADATA_DISPLAY_NAME_MAPPING[key];
  }
  return key
    .split("_")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Parses pig update metadata array (and standard pig event info) into organized rows. For TransMountain.
 */
export function parsePigUpdateMetadata(
  input: ParsePigUpdateMetadataInput
): ParsedPigUpdateMetadata {
  const result: ParsedPigUpdateMetadata = {
    rows: [],
    hasNextPoiEta: false,
    hasNextPoi: false,
    hasSpeed: false
  };

  const source = input.pigUpdateMetadata || [];
  const rows: Array<PigMetadataRow> = [];
  const existingKeys = new Set<string>();

  source.forEach(item => {
    if (!item || !item.name || item.value === null) {
      return;
    }

    const key = normalizePigMetadataKey(item.name);
    const normalizedValue = String(item.value).trim();

    //skip tool type since it is in pig run id info
    if (key === "toolType") return;

    if (!normalizedValue) {
      return;
    }

    const isTimestamp = isUnixTimestampString(normalizedValue);

    let value = normalizedValue;
    if (key.toLowerCase().includes("valve")) {
      if (value === "Check") value = "Check Valve";
      else if (value === "Block (System)") value = "Block Valve";
    }

    // hds monitor calculates these so if they are in metadata let code know to not use calculated ones
    if (key === "pigPointOfInterestEta") result.hasNextPoiEta = true;
    if (key === "nextPointOfInterest") result.hasNextPoi = true;
    if (key === "speed") result.hasSpeed = true;

    rows.push({
      name: toDisplayName(key),
      value,
      isTimestamp,
      originalKey: key,
      isKp: key === "kp",
      isCoordinates: key === "coordinates"
    });

    existingKeys.add(key);
  });

  const addRow = (key: string, value: string | number) => {
    const valueStr = String(value).trim();
    if (!valueStr || existingKeys.has(key)) return;

    rows.push({
      name: toDisplayName(key),
      value: valueStr,
      isTimestamp: isUnixTimestampString(valueStr),
      originalKey: key,
      isKp: key === "kp",
      isCoordinates: key === "coordinates"
    });
    existingKeys.add(key);

    if (key === "pigPointOfInterestEta") result.hasNextPoiEta = true;
    if (key === "nextPointOfInterest") result.hasNextPoi = true;
    if (key === "speed") result.hasSpeed = true;
  };

  const addOptionalRow = (key: string, value?: string | number | null) => {
    if (value === null || value === undefined || value === "") return;
    addRow(key, value);
  };

  // Required base fields
  addRow("channel", input.channel);
  addRow("startTime", input.startTime);
  addRow("kp", input.kp);
  addRow("coordinates", input.coordinates);

  // Optional fields
  addOptionalRow("pigRunId", input.pigRunId);
  addOptionalRow("endTime", input.endTime);
  addOptionalRow("weldId", input.weldId);
  addOptionalRow(
    "speed",
    input.speed !== null && input.speed !== undefined
      ? `${input.speed} m/second`
      : undefined
  );
  addOptionalRow("nextPointOfInterest", input.nextPoi);
  addOptionalRow("pigPointOfInterestEta", input.arrivalEstimate);

  rows.sort((a, b) => {
    const oa = getPigMetadataOrder(a.originalKey);
    const ob = getPigMetadataOrder(b.originalKey);
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

  result.rows = rows;
  return result;
}

export function renderPigMetadataRows(rows: Array<PigMetadataRow>) {
  return rows.map(row => (
    <div className={styles.Row} key={row.originalKey}>
      <Meta component="h5" size="small" dark>
        {row.name || "-"}
      </Meta>
      <Text
        className={styles.Text}
        title={row.value}
        component="span"
        size="small"
      >
        {row.isTimestamp ? (
          <DateTime
            timestamp={Number(row.value) * MILLISECONDS_IN_SECONDS}
            length="long"
          />
        ) : (
          row.value || "-"
        )}
      </Text>
    </div>
  ));
}

const EventInfo = () => {
  const { event } = useEventDetailContext();
  const { permissions } = useAuth();
  const { activeOrg } = useOrganizationContext();

  // Guard clause - exit early if no event
  if (!event) {
    return null;
  }

  let estimatedArrival: number | undefined;
  const nextPoi = event.eventMeta && event.eventMeta.nextPoi;
  const speed = event.eventMeta && event.eventMeta.speed;
  const timestamp: number | undefined = event.eventMeta
    ? event.eventMeta.timestamp
    : undefined;

  if (nextPoi && speed && timestamp) {
    const distanceInKm = nextPoi.post - event.post;
    const secondsToArrival = Math.floor((distanceInKm * METERS_IN_KM) / speed);
    estimatedArrival = timestamp + secondsToArrival;
  }

  const COORDINATE_DIGITS = 6;
  const coordinatesString = `${event.coordinates.latitude.toFixed(
    COORDINATE_DIGITS
  )},${event.coordinates.longitude.toFixed(COORDINATE_DIGITS)}`;

  const isTransMountainPig =
    activeOrg &&
    (activeOrg.id === "transmountain_expansion" ||
      activeOrg.id === "hifi-test") &&
    event.type === "pig" &&
    event.eventMeta &&
    event.eventMeta.pigUpdateMetadata;

  const parsedMeta =
    event.type === "pig" && event.eventMeta
      ? parsePigUpdateMetadata({
          pigUpdateMetadata: event.eventMeta.pigUpdateMetadata || [],
          channel: event.channel,
          startTime: event.startTime,
          kp: event.post,
          coordinates: coordinatesString,
          pigRunId: event.eventMeta.runId,
          endTime: event.endTime,
          weldId:
            event.pipeline.id === "suncor_icp"
              ? coordWeldMap(coordinatesString)
              : undefined,
          speed: event.eventMeta.speed,
          nextPoi: event.eventMeta.nextPoi
            ? `${event.eventMeta.nextPoi.type} ${event.eventMeta.nextPoi.post}`
            : undefined,
          arrivalEstimate: estimatedArrival
        })
      : null;

  const shouldShowMilePost =
    permissions.user.imperial ||
    ["enbridge_line_3", "enbridge_line_4"].includes(event.pipelineId);

  return (
    <div className={styles.Wrapper}>
      {isTransMountainPig ? (
        parsedMeta ? (
          renderPigMetadataRows(parsedMeta.rows)
        ) : null
      ) : (
        <>
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              {event.type === "pig" || event.type === "train"
                ? "RUN ID"
                : "pipeline"}
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.type === "train" && event.eventMeta
                ? event.eventMeta.railId
                : event.pipeline.name}
              {(event.type === "pig" || event.type === "train") &&
              event.eventMeta
                ? ` - Run ID ${event.eventMeta.runId}`
                : ""}
            </Text>
          </div>

          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              {shouldShowMilePost
                ? "Mile Post"
                : permissions.user.station
                ? "Station Point"
                : permissions.user.ft
                ? "Feet"
                : "KM Post"}
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {shouldShowMilePost
                ? `MP ${kpToMp(event.post)}`
                : permissions.user.station
                ? `SP ${kpToSp(event.post)}`
                : permissions.user.ft
                ? `${kpToFt(event.post)} ft`
                : `KP ${event.post}`}
            </Text>
          </div>

          {event.pipeline.id === "suncor_icp" ? (
            <div className={styles.Row}>
              <Meta component="h5" size="small" dark>
                Weld ID
              </Meta>
              <Text className={styles.Text} component="span" size="small">
                {coordWeldMap(coordinatesString)}
              </Text>
            </div>
          ) : (
            undefined
          )}

          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Start Time
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.startTime ? (
                <DateTime
                  timestamp={event.startTime * MILLISECONDS_IN_SECONDS}
                  length="long"
                />
              ) : (
                "-"
              )}
            </Text>
          </div>

          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              End Time
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.endTime ? (
                <DateTime
                  timestamp={event.endTime * MILLISECONDS_IN_SECONDS}
                  length="long"
                />
              ) : (
                "-"
              )}
            </Text>
          </div>

          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Archived
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.archived && event.archivedDate ? (
                <DateTime
                  timestamp={event.archivedDate * MILLISECONDS_IN_SECONDS}
                  length="long"
                />
              ) : (
                "-"
              )}
            </Text>
          </div>

          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Lat/Lng
            </Meta>
            {isIOS ? (
              <button type="button" onClick={copyToClipboard}>
                <Text
                  className={styles.Text}
                  component="span"
                  size="small"
                  id="copyCoordinates"
                >
                  {`${event.coordinates.latitude}, ${event.coordinates.longitude}`}
                </Text>
              </button>
            ) : (
              <a
                href={`https://maps.google.com/maps?q=loc:${event.coordinates.latitude},${event.coordinates.longitude}`}
                className={styles.Link}
                target="__blank"
              >
                <Text className={styles.Text} component="span" size="small">
                  {`${event.coordinates.latitude}, ${event.coordinates.longitude}`}
                </Text>
              </a>
            )}
          </div>

          {event.type === "pig" && event.eventMeta ? (
            <>
              <hr className={styles.Divider} />
              <div className={styles.Row}>
                <Meta component="h5" size="small" dark>
                  Speed
                </Meta>
                <Text className={styles.Text} component="span" size="small">
                  {event.eventMeta.speed}m/second
                </Text>
              </div>
              <div className={styles.Row}>
                <Meta component="h5" size="small" dark>
                  Next POI
                </Meta>
                <Text className={styles.TextPoi} component="span" size="small">
                  {event.eventMeta.nextPoi
                    ? `${event.eventMeta.nextPoi.type} ${event.eventMeta.nextPoi.post}`
                    : "N/A"}
                </Text>
              </div>
              {estimatedArrival && (
                <div className={styles.Row}>
                  <Meta component="h5" size="small" dark>
                    Arrival (EST.)
                  </Meta>
                  <Text className={styles.Text} component="span" size="small">
                    <DateTime
                      timestamp={estimatedArrival * MILLISECONDS_IN_SECONDS}
                      length="long"
                    />
                  </Text>
                </div>
              )}
            </>
          ) : null}
        </>
      )}
      {permissions.user["view-admin-event"] ? (
        <>
          <hr className={styles.Divider} />
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Channel
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.channel ? event.channel : "-"}
            </Text>
          </div>
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Guid
            </Meta>
            <Text
              className={cx(styles.Text, styles.Wrap)}
              component="span"
              size="small"
              disambiguation
            >
              {event ? event.id.toLowerCase() : "-"}
            </Text>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default EventInfo;
